import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { getUpdatedPayload } from 'utils/helpers';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, useDealManagementQuery, useUpdateDealManagementMutation } from 'api';

interface UseAssignmentEditProps {
  onCancelModal: () => void;
  open: boolean;
}

export const useAssignmentEdit = ({ onCancelModal, open }: UseAssignmentEditProps) => {
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { data: { management: dealManagement = {} } = {} } = useDealManagementQuery({ id });
  const { data: userData = [] } = useAdminUsersQuery();
  const { handleSuccess, handleError } = useNotificationMessages();
  const [updateDealManagement, { isLoading: isLoadingUpdate }] = useUpdateDealManagementMutation();

  const initialAssignment: Record<string, string> = useMemo(
    () => ({
      salesManagerId: dealManagement?.salesManager?.id,
      salesManagerAssistantId: dealManagement?.salesManagerAssistant?.id,
      purchaseManagerId: dealManagement?.purchaseManager?.id,
      purchaseManagerAssistantId: dealManagement?.purchaseManagerAssistant?.id,
      postSalesManagerId: dealManagement?.postSalesManager?.id,
      postSalesManagerAssistantId: dealManagement?.postSalesManagerAssistant?.id,
    }),
    [dealManagement]
  );

  useEffect(() => {
    if (open && dealManagement) {
      form.setFieldsValue(initialAssignment);
    }
  }, [form, dealManagement, open, initialAssignment]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onCancelModal();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      let payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialAssignment[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      payload = getUpdatedPayload(payload, initialAssignment);
      payload.dealId = id as string;
      const { data, error }: Record<string, any> = await updateDealManagement(payload);
      if (data?.success) {
        handleSuccess('Deal assignments were successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      console.error('Error updating Deal assignments:', err);
      handleError(err);
    }
  }, [updateDealManagement, handleSuccess, handleError, id, onCancel, form, initialAssignment]);

  return {
    form,
    userData,
    isLoadingUpdate,
    onFinish,
    onCancel,
  };
};
